import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ackAdminAlert, getUnackedAlertCount, listAdminAlerts, sendTestAlertEmail } from '../../lib/adminApi';
import { useAuthStore } from '../../stores/authStore';
import { adminKeys } from './adminQueryKeys';

export interface AdminAlertsFilters {
  acknowledged?: '0' | '1';
  page?: number;
  limit?: number;
}

export function useAdminAlerts(filters: AdminAlertsFilters) {
  return useQuery({
    queryKey: adminKeys.alerts.list(filters as Record<string, unknown>),
    queryFn: () => listAdminAlerts(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

/** Sidebar badge: unacknowledged alert count, polled every minute (admins only). */
export function useAdminAlertsBadge() {
  const role = useAuthStore((s) => s.user?.role);
  const query = useQuery({
    queryKey: adminKeys.alerts.unackedCount,
    queryFn: getUnackedAlertCount,
    enabled: role === 'ADMIN',
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    retry: false,
  });
  return { count: query.data?.count ?? 0, isLoading: query.isLoading };
}

export function useAckAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => ackAdminAlert(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.alerts.all });
      void queryClient.invalidateQueries({ queryKey: adminKeys.security.all });
    },
  });
}

export function useSendTestAlertEmail() {
  return useMutation({ mutationFn: sendTestAlertEmail });
}
